import axios, { AxiosResponse, AxiosError } from 'axios';
import { getFreshToken } from '../auth/tokenStore';
import {
  LoanApplicationData,
  PredictionResult,
  ScorecardData,
  ExistingCustomerData,
  ExistingCustomerResult,
  ModelPerformance,
  DataAnalysis,
  APIResponse,
  APIError,
  ModelVersion,
  TrainingStatus,
  TrainingRequest,
  ModelInfo,
  InputCatalogues,
  RefinanceInputData,
  RefinanceLookupResult,
  RefinanceResult,
  RefinanceRuleBook,
  SumaRefinanceListResult,
  UnderwritingChatRequest,
  UnderwritingChatResult,
  ExpertChatRequest,
  ExpertChatResult,
} from '../types';

// Configure axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8002',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: attach the Keycloak bearer token (refreshed if near
// expiry) so protected /api/v1/* endpoints accept the call. No-op when auth is
// disabled or the user is not logged in.
api.interceptors.request.use(
  async (config) => {
    const token = await getFreshToken();
    if (token) {
      (config.headers as any) = config.headers || {};
      (config.headers as any).Authorization = `Bearer ${token}`;
    }
    console.log(`Making ${config.method?.toUpperCase()} request to ${config.url}`);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError) => {
    const apiError: APIError = {
      message: 'An unexpected error occurred',
      code: 'UNKNOWN_ERROR',
    };

    if (error.response) {
      // Server responded with error status
      const responseData = error.response.data as any;
      apiError.message = responseData?.detail || responseData?.message || error.message;
      apiError.code = error.response.status.toString();
      apiError.details = responseData;
    } else if (error.request) {
      // Request was made but no response received
      apiError.message = 'Network error - please check your connection';
      apiError.code = 'NETWORK_ERROR';
    } else {
      // Something else happened
      apiError.message = error.message;
      apiError.code = 'REQUEST_ERROR';
    }

    return Promise.reject(apiError);
  }
);

// Helper function to handle API responses
const handleApiResponse = <T>(
  response: AxiosResponse<T>
): APIResponse<T> => {
  return {
    data: response.data,
    success: true,
    timestamp: new Date().toISOString(),
  };
};

// Helper function to handle API errors
const handleApiError = (error: APIError): APIResponse<any> => {
  return {
    error,
    success: false,
    timestamp: new Date().toISOString(),
  };
};

export class CreditRiskAPI {
  // Valid values for every categorical the form collects, read from the label
  // encoders inside the served model. Fetched rather than hard-coded because
  // hard-coded menus are what silently broke scoring: the form offered
  // Producto 'PERSONAL' and Sector_General 'SERVICIOS', neither of which the
  // model was trained on, so both were discarded on every request.
  static async getInputCatalogues(): Promise<APIResponse<InputCatalogues>> {
    try {
      const response = await api.get<InputCatalogues>('/api/v1/predict/catalogues');
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error as APIError);
    }
  }

  // Loan Default Prediction
  static async predictLoanDefault(
    data: LoanApplicationData
  ): Promise<APIResponse<PredictionResult>> {
    try {
      const response = await api.post<PredictionResult>('/api/v1/predict/loan-default', data);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error as APIError);
    }
  }

  // Credit Scorecard Generation
  static async generateScorecard(
    data: any
  ): Promise<APIResponse<ScorecardData>> {
    try {
      const response = await api.post<ScorecardData>('/api/v1/predict/scorecard', data);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error as APIError);
    }
  }

  // Existing Customer Prediction
  static async predictExistingCustomer(
    data: ExistingCustomerData
  ): Promise<APIResponse<ExistingCustomerResult>> {
    try {
      const response = await api.post<ExistingCustomerResult>('/api/v1/predict/existing-customer', data);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error as APIError);
    }
  }

  // Refinancing: the eligibility rules decide, the score only prices
  static async predictRefinance(
    data: RefinanceInputData
  ): Promise<APIResponse<RefinanceResult>> {
    try {
      const response = await api.post<RefinanceResult>('/api/v1/predict/refinance', data);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error as APIError);
    }
  }

  // The published rule book. Generated by the same code path that decides, so
  // fetching it is what keeps the screen from describing a policy nobody uses.
  static async lookupRefinanceClient(
    query: string
  ): Promise<APIResponse<RefinanceLookupResult>> {
    try {
      const response = await api.get('/api/v1/predict/refinance/lookup', {
        params: { q: query },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error as APIError);
    }
  }

  static async expertChat(
    data: ExpertChatRequest
  ): Promise<APIResponse<ExpertChatResult>> {
    try {
      const response = await api.post<ExpertChatResult>(
        '/api/v1/expert/chat',
        data,
        { timeout: 120000 }
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error as APIError);
    }
  }

  static async underwritingChat(
    data: UnderwritingChatRequest
  ): Promise<APIResponse<UnderwritingChatResult>> {
    try {
      const response = await api.post<UnderwritingChatResult>(
        '/api/v1/underwriting/chat',
        data,
        { timeout: 90000 }
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error as APIError);
    }
  }

  static async getSumaRefinanceList(): Promise<APIResponse<SumaRefinanceListResult>> {
    try {
      const response = await api.get<SumaRefinanceListResult>(
        '/api/v1/predict/refinance/suma-list'
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error as APIError);
    }
  }

  static async getRefinanceRules(): Promise<APIResponse<RefinanceRuleBook>> {
    try {
      const response = await api.get<RefinanceRuleBook>('/api/v1/predict/refinance/rules');
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error as APIError);
    }
  }

  // Model Lifecycle Management

  // Get all model versions
  static async getModelVersions(modelName: string): Promise<APIResponse<ModelVersion[]>> {
    try {
      const response = await api.get(`/api/v1/model-lifecycle/versions/${modelName}`);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error as APIError);
    }
  }

  // Get current production model
  static async getCurrentModel(modelName: string): Promise<APIResponse<ModelVersion>> {
    try {
      const response = await api.get(`/api/v1/model-lifecycle/current/${modelName}`);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error as APIError);
    }
  }

  // Promote model to stage
  static async promoteModel(
    modelName: string,
    version: string,
    stage: string,
    description?: string
  ): Promise<APIResponse<any>> {
    try {
      const response = await api.post('/api/v1/model-lifecycle/promote', {
        model_name: modelName,
        version,
        stage,
        description
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error as APIError);
    }
  }

  // Rollback model to previous version
  static async rollbackModel(
    modelName: string,
    targetVersion: string,
    reason: string
  ): Promise<APIResponse<any>> {
    try {
      const response = await api.post('/api/v1/model-lifecycle/rollback', {
        model_name: modelName,
        target_version: targetVersion,
        reason
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error as APIError);
    }
  }

  // Auto-promote model based on performance
  static async autoPromoteModel(
    modelName: string,
    performanceThreshold: number = 0.70,
    metric: string = 'auc'
  ): Promise<APIResponse<any>> {
    try {
      const response = await api.post(`/api/v1/model-lifecycle/auto-promote?model_name=${modelName}&performance_threshold=${performanceThreshold}&metric=${metric}`);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error as APIError);
    }
  }

  // Reload models from MLflow
  static async reloadModels(forceRefresh: boolean = false): Promise<APIResponse<any>> {
    try {
      const response = await api.post(`/api/v1/model-lifecycle/reload-from-mlflow?force_refresh=${forceRefresh}`);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error as APIError);
    }
  }

  // All models + their metrics (Gestión de Modelos dashboard). Returns the raw
  // response body ({ models: [...] }). Uses the shared client so the request goes
  // to the configured origin with the Keycloak bearer token attached.
  static async getModelsWithMetrics(): Promise<any> {
    const response = await api.get('/api/v1/model-lifecycle/models-with-metrics');
    return response.data;
  }

  // Training Management

  // Start training ALL models (Entrenamiento "train both" flow -> /api/v1/start).
  // Distinct from startTraining() below, which targets /api/v1/train.
  static async startTrainingAll(request: any): Promise<any> {
    const response = await api.post('/api/v1/start', request);
    return response.data;
  }

  // Start training
  static async startTraining(request: TrainingRequest): Promise<APIResponse<{ training_id: string }>> {
    try {
      const response = await api.post('/api/v1/train', request);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error as APIError);
    }
  }

  // Get training status
  static async getTrainingStatus(trainingId: string): Promise<APIResponse<TrainingStatus>> {
    try {
      const response = await api.get(`/api/v1/status/${trainingId}`);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error as APIError);
    }
  }

  // Schedule training
  static async scheduleTraining(
    modelType: string,
    frequency: string = 'weekly',
    performanceThreshold: number = 0.70,
    enabled: boolean = true
  ): Promise<APIResponse<any>> {
    try {
      const response = await api.post(`/api/v1/schedule?model_type=${modelType}&frequency=${frequency}&performance_threshold=${performanceThreshold}&enabled=${enabled}`);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error as APIError);
    }
  }

  // Model Information

  // Get model list
  static async getModelList(): Promise<APIResponse<{ models: ModelInfo[] }>> {
    try {
      const response = await api.get('/api/v1/models/');
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error as APIError);
    }
  }

  // Get specific model info
  static async getModelInfo(modelId: string): Promise<APIResponse<ModelInfo>> {
    try {
      const response = await api.get(`/api/v1/models/${modelId}`);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error as APIError);
    }
  }

  // Get model metrics
  static async getModelMetrics(
    modelId: string,
    dataset: 'train' | 'val' | 'test' = 'test'
  ): Promise<APIResponse<any>> {
    try {
      const response = await api.get(`/api/v1/models/${modelId}/metrics?dataset=${dataset}`);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error as APIError);
    }
  }

  // Get model health
  static async getModelHealth(modelId: string): Promise<APIResponse<any>> {
    try {
      const response = await api.get(`/api/v1/models/${modelId}/health`);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error as APIError);
    }
  }

  // Get model features
  static async getModelFeatures(modelId: string): Promise<APIResponse<any>> {
    try {
      const response = await api.get(`/api/v1/models/${modelId}/features`);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error as APIError);
    }
  }

  // Compare model metrics
  static async compareModelMetrics(
    modelIds: string[],
    dataset: 'train' | 'val' | 'test' = 'test'
  ): Promise<APIResponse<any>> {
    try {
      const modelIdsParam = modelIds.map(id => `model_ids=${id}`).join('&');
      const response = await api.get(`/api/v1/models/comparison/metrics?${modelIdsParam}&dataset=${dataset}`);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error as APIError);
    }
  }

  // Explainability

  // Explain prediction using SHAP
  static async explainPrediction(
    data: any,
    modelType: 'loan_default' | 'scorecard' | 'existing_customer' = 'loan_default',
    method: 'shap' | 'lime' = 'shap'
  ): Promise<APIResponse<any>> {
    try {
      const response = await api.post(`/api/v1/explain/${method}/${modelType}`, data);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error as APIError);
    }
  }

  // Data Management

  // Upload dataset for analysis
  static async uploadDataset(file: File, datasetType: string = 'training'): Promise<APIResponse<{ message: string; file_id: string }>> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('dataset_type', datasetType);

      const response = await api.post('/api/v1/data/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error as APIError);
    }
  }

  // Get dataset info
  static async getDatasetInfo(): Promise<APIResponse<any>> {
    try {
      const response = await api.get('/api/v1/data/info');
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error as APIError);
    }
  }

  // Get data analysis
  static async getDataAnalysis(
    modelType: string = 'loan_default',
    forceRefresh: boolean = false
  ): Promise<APIResponse<DataAnalysis>> {
    try {
      const params = new URLSearchParams({
        model_type: modelType,
        force_refresh: forceRefresh.toString()
      });
      const response = await api.get<DataAnalysis>(`/api/v1/analysis/data?${params}`);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error as APIError);
    }
  }

  // Get model performance
  static async getModelPerformance(modelType?: string): Promise<APIResponse<ModelPerformance>> {
    try {
      const params = modelType ? `?model_type=${modelType}` : '';
      const url = `/api/v1/analysis/performance${params}`;
      const response = await api.get<ModelPerformance>(url);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error as APIError);
    }
  }

  // System Health

  // Health check
  static async healthCheck(): Promise<APIResponse<{ status: string; timestamp: string }>> {
    try {
      const response = await api.get('/api/v1/health');
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error as APIError);
    }
  }
}

// Utility functions

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('es-PA', {
    style: 'currency',
    currency: 'PAB',
  }).format(amount);
};

export const formatPercentage = (value: number): string => {
  return new Intl.NumberFormat('es-PA', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

export const formatDate = (date: string | Date): string => {
  return new Intl.DateTimeFormat('es-PA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(date));
};

export default CreditRiskAPI;